import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { API_BASE_URL } from '../api';
import explosUrl from '../assets/GHS_PICTOGRAMS/GHS-pictogram-explos.svg?url';
import flammeUrl from '../assets/GHS_PICTOGRAMS/GHS-pictogram-flamme.svg?url';
import rondFlamUrl from '../assets/GHS_PICTOGRAMS/GHS-pictogram-rondflam.svg?url';
import bottleUrl from '../assets/GHS_PICTOGRAMS/GHS-pictogram-bottle.svg?url';
import acidUrl from '../assets/GHS_PICTOGRAMS/GHS-pictogram-acid.svg?url';
import skullUrl from '../assets/GHS_PICTOGRAMS/GHS-pictogram-skull.svg?url';
import exclamUrl from '../assets/GHS_PICTOGRAMS/GHS-pictogram-exclam.svg?url';
import silhouetteUrl from '../assets/GHS_PICTOGRAMS/GHS-pictogram-silhouette.svg?url';
import polluUrl from '../assets/GHS_PICTOGRAMS/GHS-pictogram-pollu.svg?url';

interface PublicClpTranslation {
  language: string;
  text: string;
}

interface PublicClpEmbed {
  base_language: string;
  ghs_pictograms: string[];
  translations: PublicClpTranslation[];
}

interface EmbedClpRow {
  id: string;
  code?: string;
  text: string;
}

const GHS_PICTOGRAM_URLS: Record<string, string> = {
  GHS01: explosUrl,
  GHS02: flammeUrl,
  GHS03: rondFlamUrl,
  GHS04: bottleUrl,
  GHS05: acidUrl,
  GHS06: skullUrl,
  GHS07: exclamUrl,
  GHS08: silhouetteUrl,
  GHS09: polluUrl,
};

const normalizeEmbedText = (value: string): string => value.replace(/\s+/g, ' ').trim();

const getEmbedLanguageDisplayName = (code: string): string => {
  const normalized = (code || '').trim().toLowerCase();
  if (!normalized) return '';

  try {
    return new Intl.DisplayNames(['en'], { type: 'language' }).of(normalized) || normalized.toUpperCase();
  } catch {
    return normalized.toUpperCase();
  }
};

const uniqueCodes = (rows: EmbedClpRow[]): string[] => {
  const seen = new Set<string>();
  rows.forEach((row) => {
    if (row.code) seen.add(row.code);
  });
  return [...seen];
};

const parseEmbedStatementXml = (value: string | null | undefined): EmbedClpRow[] => {
  const input = value || '';
  if (!input.trim()) return [];

  if (typeof DOMParser !== 'undefined') {
    const doc = new DOMParser().parseFromString(`<Root>${input}</Root>`, 'application/xml');
    const root = doc.documentElement;
    if (root && doc.getElementsByTagName('parsererror').length === 0) {
      const rows: EmbedClpRow[] = [];
      root.childNodes.forEach((child, index) => {
        if (child.nodeType === Node.TEXT_NODE) {
          const text = normalizeEmbedText(child.textContent || '');
          if (text) rows.push({ id: `text-${index}`, text });
          return;
        }

        if (child.nodeType !== Node.ELEMENT_NODE) return;
        const element = child as Element;
        if (!['StatementNode', 'GlossaryStatementNode'].includes(element.tagName)) return;

        const text = normalizeEmbedText(element.textContent || '');
        if (!text) return;
        rows.push({
          id: element.getAttribute('id') || `node-${index}`,
          code: element.getAttribute('code') || undefined,
          text,
        });
      });
      if (rows.length > 0) return rows;
    }
  }

  const rows: EmbedClpRow[] = [];
  const normalized = input.replace(/\r\n/g, '\n');
  const regex = /<(StatementNode|GlossaryStatementNode)\b([^>]*)>([\s\S]*?)<\/\1>/gi;
  let match: RegExpExecArray | null;
  let index = 0;
  while ((match = regex.exec(normalized))) {
    const text = normalizeEmbedText(match[3] || '');
    if (!text) continue;
    const code = /code\s*=\s*"([^"]+)"/i.exec(match[2] || '')?.[1];
    rows.push({ id: `node-${index}`, code, text });
    index += 1;
  }

  if (rows.length > 0) return rows;
  return [{ id: 'plain-text', text: normalizeEmbedText(input) }];
};

const PublicClpEmbed: React.FC = () => {
  const { publicKey } = useParams();
  const location = useLocation();
  const displayOptions = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const boolOption = (name: string, defaultValue: boolean): boolean => {
      const value = params.get(name);
      if (value === null || value === '') return defaultValue;
      return !['false', '0', 'no', 'off'].includes(value.toLowerCase());
    };
    return {
      language: (params.get('language') || '').trim().toLowerCase(),
      title: (params.get('title') || '').trim(),
      compact: boolOption('compact', params.get('layout') === 'compact'),
      showPictograms: boolOption('show_pictograms', true),
      showCodes: boolOption('show_codes', true),
      showLanguageSelector: boolOption('show_language_selector', true),
      showBranding: boolOption('branding', true),
    };
  }, [location.search]);
  const [data, setData] = useState<PublicClpEmbed | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [hoveredCode, setHoveredCode] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    document.documentElement.style.background = 'transparent';
    document.body.style.background = 'transparent';
    document.body.style.margin = '0';

    const existing = document.querySelector('meta[name="robots"]');
    const meta = existing || document.createElement('meta');
    meta.setAttribute('name', 'robots');
    meta.setAttribute('content', 'noindex');
    if (!existing) document.head.appendChild(meta);
  }, []);

  useEffect(() => {
    if (!publicKey) return;
    const controller = new AbortController();

    const load = async () => {
      setLoading(true);
      setError(false);
      try {
        const response = await fetch(`${API_BASE_URL}/public/embeds/clp/${encodeURIComponent(publicKey)}`, {
          headers: { Accept: 'application/json' },
          signal: controller.signal,
        });
        if (!response.ok) throw new Error(`Failed to load embed (${response.status})`);
        const payload: PublicClpEmbed = await response.json();
        setData(payload);
        const requestedTranslation = payload.translations.find((item) => item.language === displayOptions.language);
        const baseTranslation = payload.translations.find((item) => item.language === payload.base_language);
        setSelectedLanguage(requestedTranslation?.language || baseTranslation?.language || payload.translations[0]?.language || '');
      } catch (err) {
        if (!controller.signal.aborted) setError(true);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    load();
    return () => controller.abort();
  }, [displayOptions.language, publicKey]);

  const selectedTranslation = useMemo(
    () => data?.translations.find((item) => item.language === selectedLanguage) || data?.translations[0] || null,
    [data?.translations, selectedLanguage],
  );
  const clpRows = useMemo(() => parseEmbedStatementXml(selectedTranslation?.text), [selectedTranslation?.text]);
  const clpCodes = useMemo(() => uniqueCodes(clpRows), [clpRows]);

  if (loading) {
    return (
      <Box sx={{ minHeight: 120, display: 'grid', placeItems: 'center', bgcolor: 'transparent' }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box sx={{ p: 2, bgcolor: 'transparent' }}>
        <Alert severity="error">CLP information is not available.</Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: 'transparent',
        color: '#1f2933',
        fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
        p: displayOptions.compact ? { xs: 1, sm: 1.25 } : { xs: 1.5, sm: 2 },
      }}
    >
      <Stack spacing={displayOptions.compact ? 1 : 1.5}>
        {displayOptions.title && (
          <Typography variant="subtitle1" fontWeight={700}>
            {displayOptions.title}
          </Typography>
        )}

        {displayOptions.showPictograms && data.ghs_pictograms.length > 0 && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {data.ghs_pictograms.map((code) => {
              const pictogramUrl = GHS_PICTOGRAM_URLS[code];
              if (!pictogramUrl) return null;
              return (
                <Box
                  key={code}
                  component="img"
                  src={pictogramUrl}
                  alt={code}
                  aria-label={code}
                  title={code}
                  sx={{
                    width: displayOptions.compact ? 42 : 52,
                    height: displayOptions.compact ? 42 : 52,
                    objectFit: 'contain',
                    display: 'block',
                  }}
                />
              );
            })}
          </Stack>
        )}

        {(displayOptions.showPictograms && data.ghs_pictograms.length > 0) && <Divider />}

        {data.translations.length > 0 ? (
          <>
            {displayOptions.showLanguageSelector && data.translations.length > 1 && (
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {data.translations.map((translation) => {
                const selected = selectedTranslation?.language === translation.language;
                return (
                  <Button
                    key={translation.language}
                    type="button"
                    onClick={() => setSelectedLanguage(translation.language)}
                    variant="outlined"
                    size="small"
                    sx={{
                      borderColor: selected ? '#000' : '#c7c7c7',
                      color: selected ? '#111' : '#666',
                      bgcolor: 'transparent',
                      borderRadius: 999,
                      px: 1.5,
                      textTransform: 'none',
                      '&:hover': {
                        borderColor: '#000',
                        bgcolor: 'transparent',
                      },
                    }}
                  >
                    {getEmbedLanguageDisplayName(translation.language)}
                  </Button>
                );
              })}
            </Stack>
            )}

            {clpRows.length > 0 && (
              <Box sx={{ fontSize: displayOptions.compact ? '0.84rem' : '0.9rem' }}>
                {displayOptions.showCodes && clpCodes.length > 0 && (
                  <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mb: 1.25 }}>
                    {clpCodes.map((code) => {
                      const active = hoveredCode === code;
                      return (
                        <Chip
                          key={code}
                          size="small"
                          variant="outlined"
                          label={code}
                          onMouseEnter={() => setHoveredCode(code)}
                          onMouseLeave={() => setHoveredCode(null)}
                          sx={{
                            bgcolor: 'transparent',
                            borderColor: active ? '#000' : '#c7c7c7',
                            color: active ? '#111' : '#666',
                            cursor: 'pointer',
                            fontWeight: 600,
                            transition: 'border-color 120ms ease, color 120ms ease',
                            '&:hover': {
                              bgcolor: 'transparent',
                              borderColor: '#000',
                              color: '#111',
                            },
                            '& .MuiChip-label': {
                              px: 1,
                            },
                          }}
                        />
                      );
                    })}
                  </Stack>
                )}

                <Box sx={{ whiteSpace: 'normal', lineHeight: 1.75 }}>
                  {clpRows.map((row, index) => {
                    const active = Boolean(row.code && hoveredCode === row.code);
                    return (
                      <Box
                        key={row.id}
                        component="span"
                        onMouseEnter={() => setHoveredCode(row.code || null)}
                        onMouseLeave={() => setHoveredCode(null)}
                        sx={{
                          color: '#1f2933',
                          cursor: row.code ? 'pointer' : 'default',
                          textDecoration: active ? 'underline' : 'none',
                          textDecorationThickness: '1px',
                          textUnderlineOffset: '3px',
                        }}
                      >
                        {row.text}
                        {index < clpRows.length - 1 ? ' ' : ''}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No CLP translations are available for this product.
          </Typography>
        )}
      </Stack>

      {displayOptions.showBranding && (
      <Box sx={{ mt: 2, textAlign: 'right' }}>
        <Typography
          component="a"
          href="https://validlabels.com/"
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            fontSize: '0.65rem',
            color: '#bbb',
            textDecoration: 'none',
            '&:hover': { color: '#888' },
          }}
        >
          Created by Valid Labels
        </Typography>
      </Box>
      )}
    </Box>
  );
};

export default PublicClpEmbed;
